import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Copy, Eye, EyeOff, Snowflake, Plus, Loader2, Wallet, LifeBuoy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApp } from "@/App";
import { cn } from "@/lib/utils";
import CardFace from "@/components/CardFace";
import CardHistory from "@/components/CardHistory";
import CardsErrorDialog from "@/components/CardsErrorDialog";
import CardsScene from "@/components/cards/CardsScene";
import { PayMark } from "@/components/cards/PayMarks";
import { openSupportChat } from "@/lib/support";
import WalletBadge from "@/components/cards/WalletBadge";
import SecureGateSheet from "@/components/cards/SecureGateSheet";
import PinSetupSheet from "@/components/PinSetupSheet";

const FUND_PRESETS_MINOR = [500, 1000, 2500, 5000];
const UNLOAD_SHARES = [25, 50, 75, 100];

const CardDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cardId } = useParams();
  const { api, haptic, telegram, user, isTelegramWebApp, setBackHandler, clearBackHandler,
          pinEnabled, setPinConfigured, refreshSecurityState } = useApp();
  // Развилка перед реквизитами: нет пина — предлагаем завести, есть — сразу
  // спрашиваем (этим займётся 428-интерцептор).
  const [gate, setGate] = useState(null);   // null | "offer" | "setup"
  const location = useLocation();
  const showInlineBack = !isTelegramWebApp;

  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  // Реквизиты держим только в памяти экрана: не сохраняем и не логируем.
  const [secure, setSecure] = useState(null);
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState(null);
  const [showAllHistory, setShowAllHistory] = useState(false);

  // Какой лист открыт: пополнение, вывод или подтверждение закрытия.
  const [sheet, setSheet] = useState("");
  // Отказ — состояние экрана с объяснением и действием, а не тост, который
  // уходит через три секунды, унося единственное объяснение.
  const [error, setError] = useState(null);
  const [retryAction, setRetryAction] = useState(null);

  const [fundStr, setFundStr] = useState("10");
  const fundMinor = Math.round((parseFloat(fundStr.replace(",", ".")) || 0) * 100);
  const [unloadStr, setUnloadStr] = useState("");
  const unloadMinor = Math.round((parseFloat(unloadStr.replace(",", ".")) || 0) * 100);

  // «Назад» уводит из раздела карт СОВСЕМ, а не на список: у человека с одной
  // картой список — это экран с предложением выпустить карту, а она у него уже
  // есть. Возвращаемся ТУДА, ОТКУДА зашли — в раздел ведут два разных экрана,
  // главный и «Сервисы», и зашитый один и тот же адрес половину людей уводил
  // не туда. Метку кладёт точка входа и проносит список карт.
  const backTo = location.state?.from || "/services";
  const handleBack = useCallback(() => {
    haptic?.("light");
    navigate(backTo, { replace: true });
  }, [haptic, navigate, backTo]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  // Отказ показываем окном и запоминаем, что именно повторять. Функцию в
  // состояние кладём обёрткой: иначе useState вызовет её как ленивый
  // инициализатор вместо того, чтобы сохранить.
  const fail = useCallback((err, retry) => {
    const detail = err?.response?.data?.detail;
    setError(detail && detail.message ? detail : { message: t("cards.action_error") });
    setRetryAction(retry ? () => retry : null);
  }, [t]);

  const reload = useCallback(async () => {
    try {
      const res = await api.get("/cards/list");
      const found = (res?.data?.items || []).find((c) => c.cardId === cardId);
      setCard(found || null);
    } catch (err) {
      fail(err, null);
    } finally {
      setLoading(false);
    }
  }, [api, cardId, fail]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Лимиты и тариф приходят от провайдера, а не зашиты константами.
  useEffect(() => {
    api.get("/cards/config").then((res) => setConfig(res?.data || null)).catch(() => {});
  }, [api]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get(`/cards/${cardId}/transactions`);
      setHistory(res?.data?.items || []);
    } catch (_) {
      // История — не то, ради чего стоит ронять экран: баланс и действия
      // работают и без неё.
      setHistory([]);
    }
  }, [api, cardId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Пополнение и вывод идут около минуты. Раньше экран перечитывал баланс
  // ОДИН раз через четыре секунды — то есть заведомо раньше, чем деньги
  // доходили, — и дальше молчал. Человек видел старую сумму до тех пор, пока
  // не уходил с экрана и не возвращался: ордер завершался, а спросить о нём
  // было некому. Теперь сервер помечает карту, по которой идёт движение, и мы
  // переспрашиваем, пока метку не снимут.
  // Реквизиты теперь лежат НА карте, а карта всегда на экране — панель под ней
  // можно было хотя бы увести прокруткой. Держать номер и CVV открытыми сколько
  // угодно долго на таком месте нельзя: карта сама поворачивается обратно.
  useEffect(() => {
    if (!secure) return undefined;
    const timer = setTimeout(() => setSecure(null), 45000);
    return () => clearTimeout(timer);
  }, [secure]);

  // Бейдж Apple Wallet показываем только владельцу iPhone. Платформу берём у
  // Telegram: это точнее строки user-agent, которая в вебвью врёт (iPad с
  // iPadOS 13 представляется маком). Если Telegram молчит — падаем на UA.
  const isIPhone = (() => {
    const platform = String(window.Telegram?.WebApp?.platform || "").toLowerCase();
    if (platform) return platform === "ios";
    return /iphone|ipod/i.test(navigator.userAgent || "");
  })();

  // Закрыли — не показываем больше НИКОГДА, поэтому решение переживает
  // перезапуск и лежит в localStorage. Ключ с идентификатором: на общем
  // устройстве отказ одного человека не должен молчать за другого.
  const badgeKey = `lp:wallet-badge-hidden:${user?.id || "anon"}`;
  // По умолчанию считаем скрытым: иначе бейдж мигнёт на долю секунды до того,
  // как мы прочитаем прошлый отказ.
  const [badgeHidden, setBadgeHidden] = useState(true);
  useEffect(() => {
    try {
      setBadgeHidden(window.localStorage?.getItem(badgeKey) === "1");
    } catch (_) {
      setBadgeHidden(false);
    }
  }, [badgeKey]);

  const dismissBadge = useCallback(() => {
    haptic?.("light");
    try {
      window.localStorage?.setItem(badgeKey, "1");
    } catch (_) {
      // Приватный режим — запомнить не вышло. Скрываем хотя бы на этот сеанс:
      // спорить с человеком, который уже сказал «убери», нельзя.
    }
    setBadgeHidden(true);
  }, [badgeKey, haptic]);

  const polls = useRef(0);
  // Реквизиты открываются и из листа кошелька, а он ниже карты. Человек нажал
  // бы «Реквизиты» и не увидел разворота: карта уже уехала за верхний край.
  const cardRef = useRef(null);
  useEffect(() => {
    if (!secure) return;
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [secure]);
  useEffect(() => {
    if (!card?.pendingOrder) {
      polls.current = 0;
      return undefined;
    }
    // Около двух минут. Если за это время ордер не сел — дело не в задержке,
    // и жужжать в провайдера дальше бессмысленно.
    if (polls.current >= 24) return undefined;
    const timer = setTimeout(() => {
      polls.current += 1;
      reload();
      loadHistory();
    }, 5000);
    return () => clearTimeout(timer);
  }, [card, reload, loadHistory]);

  const money = (minor) =>
    minor === null || minor === undefined ? "—" : `$${(minor / 100).toFixed(2)}`;

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(String(value).replace(/\s/g, ""));
      haptic?.("light");
      toast.success(t("cards.copied", { what: label }));
    } catch (_) {
      toast.error(t("cards.copy_error"));
    }
  };

  // Сам запрос реквизитов, БЕЗ развилки по пину: сюда приходят, когда право
  // уже подтверждено. Разделение не косметическое — оно снимает целый класс
  // ошибок с устаревшим замыканием. Сразу после установки пина `pinEnabled`
  // в этом рендере ещё false, и общий revealSecure снова открыл бы
  // предложение поверх только что закрытого.
  const openSecure = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/cards/${cardId}/secure`);
      setSecure(res?.data || null);
      haptic?.("medium");
    } catch (err) {
      // Состояние пина во фронте могло устареть — например, его отключили с
      // другого устройства. Сервер тогда отвечает 428 pin_setup_required, и
      // это не ошибка, а та же развилка: показываем предложение, а не текст
      // «не удалось».
      if (err?.response?.data?.detail?.code === "pin_setup_required") {
        setPinConfigured?.(false);
        setGate("offer");
        return;
      }
      // Окно ввода кода закрыли крестиком — это осознанный отказ, а не сбой.
      // Тост «не удалось» здесь читался бы как ошибка приложения в ответ на
      // собственное действие человека.
      if (err?.__unlockCancelled) return;
      fail(err, openSecure);
    } finally {
      setBusy(false);
    }
  };

  const revealSecure = async () => {
    if (secure) {
      setSecure(null);
      return;
    }
    // Пина нет — показываем предложение и НЕ идём в сеть. Сервер откажет всё
    // равно (428 pin_setup_required), но упереться в отказ ради того, чтобы
    // показать то же предложение, незачем.
    if (!pinEnabled) {
      haptic?.("light");
      setGate("offer");
      return;
    }
    await openSecure();
  };

  const toggleFreeze = useCallback(async () => {
    setBusy(true);
    try {
      const action = card?.status === "frozen" ? "unfreeze" : "freeze";
      await api.post(`/cards/${cardId}/${action}`);
      haptic?.("success");
      await reload();
    } catch (err) {
      fail(err, toggleFreeze);
    } finally {
      setBusy(false);
    }
  }, [api, card?.status, cardId, fail, haptic, reload]);

  const fund = async () => {
    setBusy(true);
    try {
      await api.post(`/cards/${cardId}/fund`, { amountMinor: fundMinor });
      toast.success(t("cards.fund_started"));
      setSheet("");
      // Читаем сразу, а не через паузу: нужно не готовое число, а метка
      // движения — по ней включится опрос, который дождётся денег.
      reload();
      loadHistory();
    } catch (err) {
      fail(err, fund);
    } finally {
      setBusy(false);
    }
  };

  const unload = async () => {
    setBusy(true);
    try {
      await api.post(`/cards/${cardId}/unload`, { amountMinor: unloadMinor });
      toast.success(t("cards.unload_started"));
      setSheet("");
      reload();
      loadHistory();
    } catch (err) {
      fail(err, unload);
    } finally {
      setBusy(false);
    }
  };

  const closeCard = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/cards/${cardId}/close`);
      haptic?.("success");
      toast.success(res?.data?.pending ? t("cards.close_started") : t("cards.close_done"));
      navigate("/cards?list=1", { replace: true, state: { from: backTo } });
    } catch (err) {
      setSheet("");
      fail(err, null);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="flex h-[100dvh] flex-col items-center justify-center gap-3 bg-background px-8 text-center">
        <p className="text-sm text-muted-foreground">{t("cards.not_found")}</p>
        <button onClick={() => navigate("/cards?list=1", { replace: true, state: { from: backTo } })} className="text-sm font-semibold text-link">
          {t("cards.title")}
        </button>
      </div>
    );
  }

  const frozen = card.status === "frozen";
  const active = card.status === "active";
  // Условия по отказам: пороги и цена перевыпуска приходят с сервера (/config),
  // счёт — с карты. Без конфига плашку не рисуем: цифры в ней были бы выдуманы.
  const declineCount = Number(card.declineCount) || 0;
  const freezeAt = config?.declineFreezeAt;
  const blockAt = config?.declineBlockAt;
  const reissuePrice = config?.reissuePriceMinor;
  const available = card.balanceMinor;
  const pending = card.pendingMinor;

  const fundInvalid =
    !config || fundMinor < config.minFundMinor || fundMinor > config.maxFundMinor;
  const unloadInvalid =
    unloadMinor <= 0 || (available !== null && available !== undefined && unloadMinor > available);

  // Ключ в ответе — wallets, и это РЕШЁННЫЕ флаги. walletsProvider существует
  // только внутри бэкенда: это сырой ответ провайдера, где None значит «он
  // промолчал», и get_config его выпиливает через base.pop, достроив молчание
  // нашим env-флагом. Наружу walletsProvider не уходит никогда, так что чтение
  // config.walletsProvider давало undefined и гасило блок кошелька целиком —
  // и бейдж на iPhone, и кнопку на Android.
  const wallets = config?.wallets || {};
  const walletSupported = Boolean(wallets.applePay || wallets.googlePay);
  const isAndroid = /android/i.test(navigator.userAgent || "");
  // Долг по комиссиям, которые провайдер снял с карты помимо нас. Удержится
  // этим же пополнением, поэтому обязан быть в расчёте видимой строкой.
  const feeDebtMinor = Math.max(Number(card?.feeDebtMinor) || 0, 0);
  const fundFeeMinor = config
    ? Math.max(config.fundFlatMinor || 0, 0) +
      Math.round((fundMinor * Math.max(config.fundFeeBps || 0, 0)) / 10000) +
      Math.round((fundMinor * (config.fundMarkupPct || 0)) / 100)
    : 0;
  // Комиссия вывода удерживается ИЗ суммы: с карты уходит всё, на баланс
  // приходит меньше. Показываем оба числа, чтобы это не стало сюрпризом.
  // Ставку берём из тарифа провайдера, а не задаём здесь: она меняется без
  // нашего участия, и зашитая константа однажды начала бы врать.
  const unloadFeeKnown = config && config.unloadFeeBps >= 0 && config.unloadFlatMinor >= 0;
  // Показываем ПОЛНУЮ комиссию — провайдерскую плюс нашу. Наша удерживается из
  // зачисляемой суммы, поэтому в расчёте «сколько дойдёт» она обязана быть:
  // иначе экран пообещает больше, чем упадёт на баланс.
  const unloadFeeMinor = unloadFeeKnown
    ? config.unloadFlatMinor +
      Math.round((unloadMinor * config.unloadFeeBps) / 10000) +
      Math.round((unloadMinor * (config.unloadMarkupPct || 0)) / 100)
    : null;

  // Пополнить баланс — самая частая развязка при нехватке средств, поэтому
  // окно отказа умеет уводить туда напрямую.
  const errorExtra =
    error?.code === "balance.insufficient"
      ? { label: t("cards.issue_topup_cta"), onClick: () => navigate("/topup") }
      : error?.code === "card.frozen"
      ? { label: t("cards.action_unfreeze"), onClick: () => { setError(null); toggleFreeze(); } }
      : null;

  const openHelp = () => {
    haptic?.("light");
    openSupportChat(telegram);
  };

  const actions = [
    {
      key: "topup",
      label: t("cards.action_topup"),
      icon: Plus,
      primary: true,
      disabled: busy || !active,
      onClick: () => { haptic?.("light"); setSheet(sheet === "fund" ? "" : "fund"); },
    },
    {
      key: "help",
      label: t("cards.action_help"),
      icon: LifeBuoy,
      // Помощь работает ВСЕГДА, в том числе на замороженной и закрытой карте:
      // именно тогда она нужнее всего.
      disabled: false,
      onClick: openHelp,
    },
    {
      key: "details",
      label: t("cards.action_details"),
      icon: busy ? Loader2 : secure ? EyeOff : Eye,
      spin: busy,
      disabled: busy || !active,
      onClick: () => { haptic?.("light"); revealSecure(); },
      testId: "reveal-secure",
    },
    {
      key: "freeze",
      label: frozen ? t("cards.action_unfreeze") : t("cards.action_freeze"),
      icon: Snowflake,
      // suspended снимает только поддержка провайдера — ни заморозка, ни
      // разморозка его не берут.
      disabled: busy || card.status === "closed" || card.status === "suspended",
      onClick: () => { haptic?.("light"); toggleFreeze(); },
    },
  ];

  return (
    <CardsScene testId="card-details-page">
      <div className="lp-bar lp-topbar sticky top-0 z-30 grid grid-cols-[2rem_1fr_2rem] items-center gap-1.5 px-4 py-3">
        {showInlineBack ? (
          <button type="button" onClick={handleBack} className="lp-press inline-flex h-8 w-8 items-center justify-center text-foreground" aria-label="Back">
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : (
          <span aria-hidden="true" className="h-8 w-8" />
        )}
        <h1 className="text-center text-[17px] font-semibold tracking-tight text-foreground">{t("cards.title")}</h1>
        <span aria-hidden="true" className="h-8 w-8" />
        <span aria-hidden="true" className="lp-hair absolute inset-x-0 bottom-0 h-px" />
      </div>

      <div className="relative z-[1] flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-5 pb-[calc(2rem+var(--lp-bottom))] space-y-4">
        <div ref={cardRef}>
        <CardFace
          scheme={card.scheme}
          last4={card.last4}
          expMonth={card.expMonth}
          expYear={card.expYear}
          holder={card.nameOnCard}
          status={card.status}
          balanceMinor={available}
          statusLabel={active ? null : t(`cards.status_${card.status}`)}
          secure={secure}
          onCopy={copy}
          className="mx-auto w-full max-w-sm"
        />
        </div>

        {/* Деньги в обработке: показать только доступное и промолчать об
            остальном — значит заставить человека гадать, куда они делись. */}
        {Boolean(pending) && (
          <p className="text-center text-[11px] text-muted-foreground">
            {t("cards.balance_pending", { amount: money(pending) })}
          </p>
        )}

        {/* Пока операция в полёте, число на карте ещё не окончательное.
            Молчать об этом — значит показать старую сумму как правду. */}
        {card.pendingOrder && (
          <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t("cards.balance_updating")}
          </p>
        )}

        {/* Карта закрыта или заморожена по отказам — объясняем, что случилось и
            какой выход, прямо здесь. Раньше человек видел «Заморожена» и не
            знал, что делать дальше. */}
        {blockAt > 0 && card.status === "closed" && card.autoBlocked && (
          <div className="lp-glass space-y-2.5 p-3.5" data-testid="decline-blocked">
            <p className="text-sm font-semibold text-foreground">
              {t("cards.decline_blocked_title", { count: declineCount })}
            </p>
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              {t("cards.decline_blocked_body", { price: money(reissuePrice) })}
            </p>
            <button
              type="button"
              onClick={() => { haptic?.("light"); navigate("/cards/issue"); }}
              className="lp-press w-full rounded-[13px] bg-primary py-2.5 text-sm font-semibold text-primary-foreground"
            >
              {t("cards.reissue_cta", { price: money(reissuePrice) })}
            </button>
          </div>
        )}
        {card.status === "suspended" && (
          <div className="lp-glass space-y-2.5 p-3.5" data-testid="card-suspended">
            <p className="text-sm font-semibold text-foreground">{t("cards.suspended_title")}</p>
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              {t("cards.suspended_body", { price: money(reissuePrice) })}
            </p>
          </div>
        )}
        {blockAt > 0 && frozen && card.autoFrozen && (
          <div className="lp-glass space-y-2.5 p-3.5" data-testid="decline-frozen">
            <p className="text-sm font-semibold text-foreground">
              {t("cards.decline_frozen_title", { count: declineCount })}
            </p>
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              {t("cards.decline_frozen_body", {
                left: Math.max(blockAt - declineCount, 1),
                blockAt,
                price: money(reissuePrice),
              })}
            </p>
          </div>
        )}
        {/* Правило видно всегда, а не только после первого отказа: иначе
            человек узнаёт о нём, когда запас уже потрачен. */}
        {blockAt > 0 && (active || (frozen && !card.autoFrozen)) && (
          <div className="lp-glass px-3.5 py-2.5 text-[12px] leading-relaxed text-muted-foreground" data-testid="decline-counter">
            <span className="font-semibold text-foreground">
              {t("cards.decline_counter_head", { count: declineCount, blockAt })}
            </span>{" "}
            {declineCount < freezeAt
              ? t("cards.decline_counter", { left: freezeAt - declineCount, freezeAt, blockAt })
              : t("cards.decline_counter_last", { left: Math.max(blockAt - declineCount, 1) })}
          </div>
        )}

        <div className="grid grid-cols-4 gap-2">
          {actions.map(({ key, label, icon: Icon, primary, disabled, onClick, spin, testId }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              disabled={disabled}
              data-testid={testId}
              className={cn(
                "lp-press flex min-h-[62px] flex-col items-center justify-center gap-1.5 rounded-[15px] px-1 py-2.5 text-[10px] font-medium leading-tight disabled:opacity-40",
                primary
                  ? "bg-primary text-primary-foreground shadow-[0_8px_18px_-10px_hsl(var(--primary))]"
                  : "lp-tile text-foreground"
              )}
            >
              <Icon className={cn("h-4 w-4", primary ? "text-primary-foreground" : "text-link", spin && "animate-spin")} />
              <span className="text-center">{label}</span>
            </button>
          ))}
        </div>

        {/* Провайдер не даёт push provisioning, поэтому «в одно касание» карта в
            кошелёк не попадает ни у Apple, ни у Google. Кнопка честно ведёт к
            инструкции с реквизитами, а не обещает то, чего не будет. */}
        {gate === "offer" && (
          <SecureGateSheet
            onSetup={() => { haptic?.("light"); setGate("setup"); }}
            onClose={() => setGate(null)}
          />
        )}

        {gate === "setup" && (
          <PinSetupSheet
            api={api}
            user={user}
            mode="create"
            onClose={() => setGate(null)}
            onSuccess={async () => {
              setPinConfigured?.(true);
              await refreshSecurityState?.();
              setGate(null);
              // Человек шёл за реквизитами, а не за настройкой безопасности:
              // доводим до цели сами, без второго нажатия. Код только что
              // придуман, поэтому сессия уже разблокирована — идём напрямую в
              // запрос, минуя проверку pinEnabled: в этом рендере она ещё
              // видит старое значение.
              openSecure();
            }}
            onLinkEmail={() => setPinConfigured?.(true)}
          />
        )}

        {walletSupported && active && isIPhone && !badgeHidden && (
          <WalletBadge
            onAdd={() => { haptic?.("light"); setSheet(sheet === "wallet" ? "" : "wallet"); }}
            onDismiss={dismissBadge}
          />
        )}

        {walletSupported && active && !isIPhone && (
          <button
            type="button"
            onClick={() => { haptic?.("light"); setSheet(sheet === "wallet" ? "" : "wallet"); }}
            className="lp-press flex min-h-[44px] w-full items-center justify-center gap-2 rounded-[13px] border border-white/15 bg-black/60 px-4 text-[12px] font-semibold text-white backdrop-blur-[14px] dark:bg-black/60"
            data-testid="wallet-open"
          >
            <PayMark android={isAndroid} className="h-4 w-auto" />
            <span className="opacity-40">|</span>
            {isAndroid ? t("cards.wallet_add_google") : t("cards.wallet_add_apple")}
          </button>
        )}

        {sheet === "fund" && (
          <div className="lp-glass space-y-3 p-3.5">
            <div className="text-sm font-semibold text-foreground">{t("cards.fund_title")}</div>
            <label className="flex items-baseline gap-1 border-b border-[var(--lp-hair)] pb-2">
              <span className="text-2xl font-semibold text-muted-foreground">$</span>
              <input
                type="text" inputMode="decimal" value={fundStr}
                onChange={(e) => setFundStr(e.target.value.replace(/[^\d.,]/g, "").slice(0, 9))}
                placeholder="0" aria-label={t("cards.fund_title")}
                className="w-full bg-transparent text-2xl font-semibold tabular-nums text-foreground outline-none placeholder:text-muted-foreground"
                data-testid="fund-amount-input"
              />
            </label>
            <div className="grid grid-cols-4 gap-2">
              {FUND_PRESETS_MINOR.map((value) => (
                <button key={value} onClick={() => setFundStr(String(value / 100))} disabled={busy}
                  className={cn(
                    "lp-press min-h-[34px] rounded-[11px] px-2 text-sm font-semibold tabular-nums disabled:opacity-50",
                    fundMinor === value ? "bg-primary text-primary-foreground" : "lp-tile text-foreground"
                  )}>
                  ${value / 100}
                </button>
              ))}
            </div>
            <p className={cn("text-xs", fundInvalid ? "text-destructive-fg" : "text-muted-foreground")}>
              {config
                ? t("cards.issue_limits", {
                    min: `$${(config.minFundMinor / 100).toFixed(2)}`,
                    max: `$${(config.maxFundMinor / 100).toFixed(0)}`,
                  })
                : t("cards.fund_hint")}
            </p>
            {config && !fundInvalid && (
              <div className="space-y-1.5 border-t border-[var(--lp-hair)] pt-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{t("cards.fund_to_card")}</span>
                  <span className="tabular-nums text-foreground">{money(fundMinor)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{t("cards.issue_fee")}</span>
                  <span className="tabular-nums text-foreground">{money(fundFeeMinor)}</span>
                </div>
                {feeDebtMinor > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{t("cards.fee_debt")}</span>
                    <span className="tabular-nums text-foreground">{money(feeDebtMinor)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm font-semibold">
                  <span className="text-foreground">{t("cards.issue_total")}</span>
                  <span className="tabular-nums text-link">
                    {money(fundMinor + fundFeeMinor + feeDebtMinor)}
                  </span>
                </div>
              </div>
            )}
            <button onClick={fund} disabled={busy || fundInvalid}
              className={cn("lp-press w-full rounded-[13px] px-4 py-3 text-sm font-semibold",
                busy || fundInvalid ? "lp-tile text-muted-foreground" : "bg-primary text-primary-foreground")}
              data-testid="fund-submit">
              {t("cards.fund_submit")}
            </button>
          </div>
        )}

        {sheet === "unload" && (
          <div className="lp-glass space-y-3 p-3.5" data-testid="unload-sheet">
            <div className="text-sm font-semibold text-foreground">{t("cards.unload_title")}</div>
            <label className="flex items-baseline gap-1 border-b border-[var(--lp-hair)] pb-2">
              <span className="text-2xl font-semibold text-muted-foreground">$</span>
              <input
                type="text" inputMode="decimal" value={unloadStr}
                onChange={(e) => setUnloadStr(e.target.value.replace(/[^\d.,]/g, "").slice(0, 9))}
                placeholder="0" aria-label={t("cards.unload_title")}
                className="w-full bg-transparent text-2xl font-semibold tabular-nums text-foreground outline-none placeholder:text-muted-foreground"
                data-testid="unload-amount-input"
              />
            </label>
            <div className="grid grid-cols-4 gap-2">
              {UNLOAD_SHARES.map((share) => (
                <button key={share} disabled={busy || !available}
                  onClick={() => setUnloadStr((((available || 0) * share) / 100 / 100).toFixed(2))}
                  className="lp-press lp-tile min-h-[34px] rounded-[11px] px-2 text-sm font-semibold tabular-nums text-foreground disabled:opacity-50">
                  {share === 100 ? t("cards.unload_all") : `${share}%`}
                </button>
              ))}
            </div>
            <div className="space-y-1.5 border-t border-[var(--lp-hair)] pt-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{t("cards.unload_from_card")}</span>
                <span className="tabular-nums text-foreground">{money(unloadMinor)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{t("cards.unload_fee")}</span>
                <span className="tabular-nums text-foreground">
                  {unloadFeeKnown ? `−${money(unloadFeeMinor)}` : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm font-semibold">
                <span className="text-foreground">{t("cards.unload_to_balance")}</span>
                <span className="tabular-nums text-link">
                  {unloadFeeKnown ? money(Math.max(unloadMinor - unloadFeeMinor, 0)) : "—"}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t("cards.unload_hint")}</p>
            <button onClick={unload} disabled={busy || unloadInvalid}
              className={cn("lp-press w-full rounded-[13px] px-4 py-3 text-sm font-semibold",
                busy || unloadInvalid ? "lp-tile text-muted-foreground" : "bg-primary text-primary-foreground")}
              data-testid="unload-submit">
              {t("cards.unload_submit")}
            </button>
          </div>
        )}

        {sheet === "wallet" && (
          <div className="lp-glass space-y-3 p-3.5">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-link" />
              <span className="text-sm font-semibold text-foreground">{t("cards.wallet_title")}</span>
            </div>
            <ol className="list-decimal space-y-1 pl-4 text-xs leading-relaxed text-muted-foreground">
              <li>{t("cards.wallet_step_open")}</li>
              <li>{t("cards.wallet_step_enter")}</li>
              <li>{t("cards.wallet_step_code")}</li>
            </ol>
            <div className="grid grid-cols-2 gap-2">
              {/* revealSecure сам по себе переключатель: увидев secure, он его
                  гасит. Здесь стоял guard `if (!secure)`, который съедал именно
                  обратный ход — реквизиты открывались и больше не закрывались. */}
              <button onClick={() => { haptic?.("light"); revealSecure(); }} disabled={busy}
                className="lp-press rounded-[13px] bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                data-testid="wallet-reveal">
                {secure ? t("cards.action_details_hide") : t("cards.action_details")}
              </button>
              <a href={isAndroid ? "https://wallet.google.com" : "https://support.apple.com/ru-ru/HT204506"}
                target="_blank" rel="noopener noreferrer"
                className="lp-press lp-tile flex items-center justify-center rounded-[13px] px-3 py-2.5 text-sm font-semibold text-foreground">
                {isAndroid ? t("cards.wallet_open_google") : t("cards.wallet_open_apple")}
              </a>
            </div>
          </div>
        )}

        <CardHistory
          items={history}
          last4={card.last4}
          limit={4}
          showAll={showAllHistory}
          onShowAll={() => setShowAllHistory((v) => !v)}
        />

        {/* Вывод остатка ничего не разрушает и обратим — обычная строка, без
            красного. Красный отдан единственному необратимому шагу, и то не
            здесь, а в окне подтверждения. */}
        {active && (
          <div className="lp-glass overflow-hidden">
            <button
              type="button"
              onClick={() => setSheet(sheet === "unload" ? "" : "unload")}
              disabled={busy || !available}
              className="lp-press flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left disabled:opacity-50"
              data-testid="unload-open"
            >
              <span>
                <span className="block text-sm text-foreground">{t("cards.action_unload")}</span>
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  {t("cards.unload_subtitle")}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          </div>
        )}

        {card.status !== "closed" && (
          <button
            type="button"
            onClick={() => setSheet("close")}
            disabled={busy}
            className="w-full py-3 text-center text-xs text-muted-foreground underline decoration-muted-foreground/40 underline-offset-4 disabled:opacity-50"
            data-testid="close-open"
          >
            {t("cards.action_close")}
          </button>
        )}
      </div>

      {/* Закрытие необратимо, поэтому спрашиваем отдельно и показываем, сколько
          вернётся. Комиссии за возврат остатка при закрытии провайдер не берёт —
          об этом сказано прямо, иначе человек сначала выведет за 1%, а потом
          закроет, и заплатит зря. */}
      {sheet === "close" && (
        <div className="fixed inset-0 z-[95] flex items-end justify-center sm:items-center">
          <span className="lp-scrim" onClick={() => (busy ? null : setSheet(""))} aria-hidden="true" />
          <div className="lp-sheet relative w-full max-w-md p-6 pb-[calc(1.5rem+var(--lp-bottom))] text-center sm:rounded-3xl sm:pb-6">
            <h3 className="text-lg font-semibold text-foreground">{t("cards.close_title")}</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground/80">{t("cards.close_body")}</p>

            <div className="lp-tile mt-4 space-y-1.5 rounded-xl px-4 py-3 text-left">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{t("cards.close_residual")}</span>
                <span className="tabular-nums text-foreground">{money(available)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{t("cards.close_fee")}</span>
                <span className="tabular-nums text-foreground">{money(0)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-[var(--lp-hair)] pt-1.5 text-sm font-semibold">
                <span className="text-foreground">{t("cards.close_back")}</span>
                <span className="tabular-nums text-link">{money(available)}</span>
              </div>
            </div>
            {Boolean(pending) && (
              <p className="mt-2 text-[11px] text-muted-foreground">{t("cards.close_pending_note")}</p>
            )}

            <div className="mt-5 space-y-2">
              <button type="button" onClick={closeCard} disabled={busy}
                className="lp-press w-full rounded-[13px] bg-destructive px-4 py-3.5 text-sm font-semibold text-destructive-foreground disabled:opacity-50"
                data-testid="close-confirm">
                {busy ? t("cards.close_progress") : t("cards.close_confirm")}
              </button>
              <button type="button" onClick={() => setSheet("")} disabled={busy}
                className="lp-press lp-tile w-full rounded-[13px] px-4 py-3 text-sm font-semibold text-foreground">
                {t("cards.close_cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      <CardsErrorDialog
        error={error}
        onClose={() => { setError(null); setRetryAction(null); }}
        onRetry={retryAction ? () => { const fn = retryAction; setError(null); setRetryAction(null); fn(); } : null}
        extraAction={errorExtra}
      />
    </CardsScene>
  );
};

export default CardDetailsPage;
